/**
 * AI Tools Module
 *
 * Re-exports all tool-related functionality for the Marta AI assistant.
 * This module provides the ReAct pattern implementation for tool calling.
 */

// Types
export type {
  ToolDefinition,
  ToolCall,
  ToolResult,
  PendingConfirmation,
  ToolCallStatus,
  MessageToolCall,
  ToolSchema,
  ToolExecutionOptions,
  ToolCategory,
  ToolOperation,
} from './types';

export {
  DEFAULT_EXECUTION_OPTIONS,
  CONFIRMATION_PATTERNS,
  isConfirmation,
  isCancellation,
} from './types';

// Registry
export {
  toolRegistry,
  registerTools,
  getTool,
  getToolsSchema,
  toolRequiresConfirmation,
} from './registry';

// Executor
export {
  parseToolCall,
  hasToolCall,
  extractTextBeforeToolCall,
  requiresConfirmation,
  getToolDefinition,
  createPendingConfirmation,
  executeToolCall,
  formatToolResult,
  createToolResultMessage,
  getAvailableTools,
  getToolsByCategory,
  validateToolParameters,
  describeToolCall,
} from './executor';

// Tool definitions - Projects
export {
  projectTools,
  projectToolExecutors,
  searchProjectsTool,
  getProjectTool,
  createProjectTool,
  updateProjectTool,
  deleteProjectTool,
  searchTasksTool,
  getTaskTool,
  createTaskTool,
  updateTaskTool,
  markTaskDoneTool,
  deleteTaskTool,
  listMilestonesTool,
  createMilestoneTool,
  deleteMilestoneTool,
} from './projectTools';

// Tool definitions - Calendar
export {
  calendarTools,
  calendarToolExecutors,
  listEventsTool,
  searchEventsTool,
  createEventTool,
  createReminderTool,
  deleteEventTool,
  getTodaysEventsTool,
  getUpcomingEventsTool,
} from './calendarTools';

// Tool definitions - Contacts
export {
  contactTools,
  contactToolExecutors,
  searchContactsTool,
  getContactTool,
  createContactTool,
  deleteContactTool,
  findContactByNameTool,
} from './contactTools';

// Tool definitions - Accounting
export {
  accountingTools,
  accountingToolExecutors,
  searchCustomersTool,
  getCustomerTool,
  createCustomerTool,
  searchInvoicesTool,
  getInvoiceTool,
  createInvoiceTool,
  getInvoiceSummaryTool,
  listBankAccountsTool,
  getBankBalanceTool,
} from './accountingTools';

// Tool definitions - Hub (Communications)
export {
  hubTools,
  hubToolExecutors,
} from './hubTools';

// Tool definitions - Support
export {
  supportTools,
  supportToolExecutors,
} from './supportTools';

// =============================================================================
// INITIALIZATION
// =============================================================================

import { registerTools } from './registry';
import { projectTools } from './projectTools';
import { calendarTools } from './calendarTools';
import { contactTools } from './contactTools';
import { accountingTools } from './accountingTools';
import { hubTools } from './hubTools';
import { supportTools } from './supportTools';

/**
 * Initialize all tools in the registry.
 * Call this once at application startup.
 */
export function initializeTools(): void {
  registerTools(projectTools);
  registerTools(calendarTools);
  registerTools(contactTools);
  registerTools(accountingTools);
  registerTools(hubTools);
  registerTools(supportTools);
}

/**
 * Get all tool definitions
 */
export function getAllToolDefinitions() {
  return [
    ...projectTools,
    ...calendarTools,
    ...contactTools,
    ...accountingTools,
    ...hubTools,
    ...supportTools,
  ];
}

/**
 * Get count of available tools
 */
export function getToolCount(): number {
  return getAllToolDefinitions().length;
}
